import React, { useState, useEffect, useRef } from 'react';
// Firebase upload now happens server-side, so no Firebase imports needed

const VideoGeneratorRemote = ({ tweetImage, tweetData, isGenerating, onVideoComplete, user, autoStart = false, onProgressUpdate }) => {
  const jobSubmittedRef = useRef(false);
  const [videoBlob, setVideoBlob] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [status, setStatus] = useState('');

  // Configuration
  const SERVER_URL = '/api/video';

  // Auto-start video generation when component mounts with autoStart
  useEffect(() => {
    if (autoStart && tweetImage && !jobSubmittedRef.current) {
      jobSubmittedRef.current = true;
      generateVideo();
    }
  }, [autoStart, tweetImage]);

  const generateVideo = async () => {
    if (!tweetImage) return;
    
    setIsProcessing(true);
    setProgress(0);
    setStatus('Submitting video job...');
    
    try {
      // Process audio selection for server
      let audioSelection = null;
      if (tweetData?.audioSelection) {
        console.log('🔍 Raw audioSelection from tweetData:', tweetData.audioSelection);
        
        const { type, file, libraryFilename, libraryUrl } = tweetData.audioSelection;
        audioSelection = { type, libraryFilename, libraryUrl };
        
        // Convert custom audio file to base64 if needed
        if (type === 'custom' && file) {
          const audioBase64 = await convertFileToBase64(file);
          audioSelection.audioData = audioBase64; // Server expects 'audioData', not 'file'
          console.log('🎵 Sending custom audio to server:', {
            type: audioSelection.type,
            hasAudioData: !!audioSelection.audioData,
            audioDataSize: audioSelection.audioData ? audioSelection.audioData.length : 0
          });
        } else if (type === 'custom') {
          console.log('⚠️ Custom audio type but no file found in audioSelection:', tweetData.audioSelection);
        }
      } else {
        console.log('⚠️ No audioSelection found in tweetData:', tweetData);
      }
      
      console.log('🚀 Final audioSelection being sent:', audioSelection);
      
      // Submit video generation job to remote server
      const response = await fetch(`${SERVER_URL}/render-video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tweetImage,
          tweetData,
          audioSelection,
          circleOverlays: tweetData?.circleOverlays || [], // Add circle overlays
          backgroundSelection: tweetData?.backgroundSelection || null, // Add background selection
          userId: user?.uid // Add user ID for server-side Firebase upload
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to submit video job');
      }

      const jobResult = await response.json();
      setCurrentJobId(jobResult.jobId);
      setStatus('Video job queued...');
      
      // Poll for job completion
      await pollJobStatus(jobResult.jobId);
      
    } catch (error) {
      console.error('Error generating video:', error);
      setStatus('Error generating video');
      setIsProcessing(false);
      onVideoComplete();
    }
  };

  // Helper function to convert file to base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const pollJobStatus = async (jobId) => {
    let attempts = 0;
    const maxAttempts = 750; // 10 minutes max (0.8 second intervals)
    
    while (attempts < maxAttempts) {
      try {
        const response = await fetch(`${SERVER_URL}/job-status/${jobId}`);
        
        if (!response.ok) {
          throw new Error('Failed to check job status');
        }
        
        const status = await response.json();
        
        // Update progress
        setProgress(status.progress || 0);
        if (onProgressUpdate) onProgressUpdate(status.progress || 0);
        
        if (status.status === 'completed') {
          setStatus('Downloading video...');
          await downloadVideo(jobId, status.firebase); // Pass Firebase URLs
          break;
        } else if (status.status === 'failed') {
          throw new Error(status.error || 'Video generation failed');
        } else if (status.status === 'active') {
          setStatus('Generating video...');
        } else if (status.status === 'waiting') {
          setStatus('Waiting in queue...');
        }
        
        // Wait 0.8 seconds before next check
        await new Promise(resolve => setTimeout(resolve, 800));
        attempts++;
        
      } catch (error) {
        console.error('Error checking job status:', error);
        setStatus('Error checking video status');
        setIsProcessing(false);
        onVideoComplete();
        return;
      }
    }
    
    if (attempts >= maxAttempts) {
      setStatus('Video generation timed out');
      setIsProcessing(false);
      onVideoComplete();
    }
  };

  const triggerLocalDownload = (videoBlob, tweetData) => {
    const url = URL.createObjectURL(videoBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `twizzler-${tweetData?.username || 'tweet'}-${Date.now()}.mp4`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadVideo = async (jobId, firebaseUrls = null) => {
    try {
      const response = await fetch(`${SERVER_URL}/download/${jobId}`);
      if (!response.ok) {
        throw new Error('Failed to download video');
      }
      const videoBlob = await response.blob();
      setVideoBlob(videoBlob);
      
      // Immediately download to user device
      triggerLocalDownload(videoBlob, tweetData);
      
      // Video is already uploaded to Firebase by the server
      if (firebaseUrls) {
        setStatus('Video saved to cloud and downloaded!');
      } else {
        setStatus('Video downloaded successfully!');
      }
      
      setProgress(100);
      if (onProgressUpdate) onProgressUpdate(100);
      setIsProcessing(false);
      onVideoComplete();
      
    } catch (error) {
      console.error('Error downloading video:', error);
      setStatus('Error downloading video');
      setIsProcessing(false);
      onVideoComplete();
    }
  };
  
  const downloadVideoLocally = () => {
    if (videoBlob) {
      const url = URL.createObjectURL(videoBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `twizzler-${tweetData?.username || 'tweet'}-${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="video-generator-inline">
      {(isProcessing) && (
        <div className="generation-progress">
          <div className="progress-bar-wrapper">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <span className="progress-text">{Math.round(progress)}%</span>
          </div>
          <p className="progress-status">
            {status || 'Processing...'}
          </p>
          {currentJobId && (
            <small className="job-id">Job ID: {currentJobId}</small>
          )}
        </div>
      )}
      
      {!isProcessing && videoBlob && (
        <div className="video-complete">
          <p className="completion-message">✅ Video generated successfully!</p>
          <button 
            className="download-button"
            onClick={downloadVideoLocally}
            type="button"
          >
            📥 Download Video
          </button>
        </div>
      )}
    </div>
  );
};

export default VideoGeneratorRemote; 